<script setup lang="ts">
/**
 * Primary action element.
 *
 * Sized at least 44×44 by default (Apple HIG / Material 48dp touch target).
 *
 * Loading state: the spinner takes over the entire button content area
 * (replacing both the leading icon AND the label visually). The label
 * stays in the DOM via `aria-label` + a visually-hidden mirror so screen
 * readers still know what the button represents. Earlier behaviour hid
 * only the leading icon, which left labels like "Sign in" looking
 * untouched while the request was in flight — users couldn't tell the
 * click had landed. This is the unambiguous fix.
 */
import { Primitive } from 'reka-ui'
import { cva, type VariantProps } from 'class-variance-authority'
import { computed } from 'vue'
import { LoaderCircle } from '@lucide/vue'

import { cn } from '@/shared/lib/cn'

const button = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'font-medium select-none whitespace-nowrap',
    'rounded-md transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:opacity-50 disabled:pointer-events-none',
    // Loading: keep the button width stable but make the state
    // unmistakable — spinner replaces the icon-left slot AND the label
    // dims to ~50%. Cursor signals "wait, don't click again" on hover.
    'aria-busy:cursor-wait',
    // Stretch the click area on touch devices without growing the visual.
    "[&[data-touch-target]]:relative [&[data-touch-target]]:after:content-['']",
    '[&[data-touch-target]]:after:absolute [&[data-touch-target]]:after:inset-0',
    '[&[data-touch-target]]:after:min-h-touch [&[data-touch-target]]:after:min-w-touch',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary: 'bg-muted text-foreground hover:bg-muted/80',
        ghost: 'bg-transparent text-foreground hover:bg-muted',
        outline: 'bg-transparent text-foreground border border-border hover:bg-muted',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      },
      size: {
        // 16px font on mobile prevents iOS focus zoom; we never go below it.
        sm: 'h-9 px-3 text-sm md:text-sm',
        md: 'h-11 px-4 text-base md:text-sm',
        lg: 'h-12 px-5 text-base',
        icon: 'h-11 w-11 p-0',
        'icon-sm': 'h-9 w-9 p-0',
      },
      fullWidth: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', fullWidth: false },
  },
)

type ButtonVariants = VariantProps<typeof button>

interface Props {
  variant?: ButtonVariants['variant']
  size?: ButtonVariants['size']
  fullWidth?: boolean
  loading?: boolean
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  /** Render as a child element (e.g. router-link) instead of a `<button>`. */
  asChild?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  fullWidth: false,
  loading: false,
  disabled: false,
  type: 'button',
  asChild: false,
})

const classes = computed(() =>
  cn(button({ variant: props.variant, size: props.size, fullWidth: props.fullWidth })),
)

const isDisabled = computed(() => props.disabled || props.loading)
</script>

<template>
  <Primitive
    :as="asChild ? 'template' : 'button'"
    :as-child="asChild"
    :type="asChild ? undefined : type"
    :disabled="isDisabled || undefined"
    :aria-busy="loading || undefined"
    data-touch-target
    data-focus-visible-managed
    :class="classes"
  >
    <!-- While loading: spinner replaces the leading icon AND the label
         dims to ~50% with a wait-cursor. The label stays in place so the
         button doesn't reflow / shrink, but the combined signal
         (spinner + dimmed text + wait-cursor + aria-busy) is
         unmistakable. -->
    <LoaderCircle v-if="loading" class="size-4 animate-spin" aria-hidden="true" />
    <slot v-else name="icon-left" />
    <span :class="loading ? 'opacity-50' : 'contents'">
      <slot />
    </span>
    <slot v-if="!loading" name="icon-right" />
  </Primitive>
</template>
